import React, { useState, useEffect, useRef } from 'react';
import { injectedComponent, Text } from '@base/core';
import { useLocaleSwitchApp } from '@base/hooks/i18n';
import { useSessionStorage } from '@base/hooks/useSessionStorage';

function UnauthorizedUserWebMenu({ onLogin, onSignUp }) {
  return (
    <div className="header__loginBlock">
      <a href="#" className="link" onClick={onLogin}>
        <Text>navigation.login</Text>
      </a>
      <button className="primaryBtn primaryBtn-small js-registrationBtn" onClick={onSignUp}>
        <Text>navigation.sign_up</Text>
      </button>
    </div>
  );
}

function UnauthorizedUserMobileMenu({ onLogin, onSignUp }) {
  return (
    <div className="header__loginBlock-mobile ">
      <button
        className="primaryBtn btn-fullwidth js-registrationBtn loginBlock-btn"
        onClick={onSignUp}
      >
        <Text>navigation.sign_up</Text>
      </button>
      <button className="secondaryBtn btn-fullwidth loginBlock-btn" onClick={onLogin}>
        <Text>navigation.login</Text>
      </button>
    </div>
  );
}

export default injectedComponent(
  {
    name: 'HeaderBar',
    services: ['access', 'dialog', 'settings', 'user', 'notifier'],
    hooks: ['useDetectPredefinedNotifications'],
    components: ['LoginDialog', 'SignUpDialog', 'FAQDialog', 'WebHeaderUserInfo', 'Spinner'],
  },
  function HeaderBar({
    services: {
      access,
      dialog,
      settings: {
        animation: { userAccessDetecting },
      },
      user,
      notifier,
    },
    hooks: { useDetectPredefinedNotifications },
    components: { LoginDialog, SignUpDialog, FAQDialog, WebHeaderUserInfo, Spinner },
  }) {
    const navRef = useRef();
    const mobileMenuBtnRef = useRef();
    const detectPredefinedNotifications = useDetectPredefinedNotifications();
    const switchApp = useLocaleSwitchApp();

    const [isUserMenuOpened, setUserMenuState] = useState(false);
    const storage = useSessionStorage(user.email);

    const handleLoginDialog = () => {
      dialog.open(LoginDialog);
    };

    const handleSignUpDialog = () => {
      dialog.open(SignUpDialog);
    };

    const handleFAQDialog = () => {
      dialog.open(FAQDialog);
    };

    const handleDialogOpen = (dialogId) => () => {
      switchApp('my/', { dialog: dialogId });
    };

    const handleLogout = () => {
      setUserMenuState(false);
      notifier.closeAll();
      if (storage) {
        storage.clearAppKeys();
      }
      access.logout();
    };

    const handleMobileMenuToggle = () => {
      const navigation = navRef.current;
      const mobileMenuBtn = mobileMenuBtnRef.current;
      if (mobileMenuBtn.classList.contains('menu__mobile-opened')) {
        mobileMenuBtn.classList.remove('menu__mobile-opened');
        navigation.classList.add('header__navlist-closed');
        navigation.classList.remove('header__navlist-opened');
        return false;
      }
      mobileMenuBtn.classList.add('menu__mobile-opened');
      navigation.classList.toggle('header__navlist-closed', false);
      navigation.classList.add('header__navlist-opened');
    };

    useEffect(() => {
      detectPredefinedNotifications();
    }, []);

    return (
      <header className="headerContainer">
        <div className="header">
          <nav className="header__navigation">
            <div className="header__navigation__logo">
              <div className="main-logo">
                <img src="/public/images/logo_black.png" />
                <Text>site.title</Text>
              </div>
            </div>
            <span className="link" onClick={handleFAQDialog}>
              <Text>navigation.faq__desktop</Text>
            </span>
          </nav>
          {!access.detected ? (
            <div className="HeaderBar__SpinnerContainer">
              <Spinner animationData={userAccessDetecting} />
            </div>
          ) : access.hasAnyAccess() ? (
            <WebHeaderUserInfo
              hasMenu={true}
              isUserMenuOpened={isUserMenuOpened}
              setUserMenuState={setUserMenuState}
              openDialog={handleDialogOpen}
              logout={handleLogout}
            />
          ) : (
            <UnauthorizedUserWebMenu onLogin={handleLoginDialog} onSignUp={handleSignUpDialog} />
          )}
        </div>
        <nav className="header__navigation-mobile">
          <div className="header__logo">
            <div className="main-logo">
              <img src="/public/images/logo_black.png" />
              <Text>site.title</Text>
            </div>
          </div>
          <div className="header__buttonBlock-mobile">
            {access.hasAnyAccess() ? (
              <WebHeaderUserInfo
                isUserMenuOpened={isUserMenuOpened}
                setUserMenuState={setUserMenuState}
                openDialog={handleDialogOpen}
                logout={handleLogout}
              />
            ) : (
              <button
                className="primaryBtn primaryBtn-small js-registrationBtn"
                onClick={handleSignUpDialog}
              >
                <Text>navigation.sign_up</Text>
              </button>
            )}
            <button
              ref={mobileMenuBtnRef}
              className="menu__mobile"
              onClick={handleMobileMenuToggle}
            >
              <div className="menu__icon"></div>
            </button>
          </div>
        </nav>
        <div ref={navRef} className="header__navlist-closed">
          <div className="header__links-mobile">
            <span onClick={handleFAQDialog} className="header__link-mobile">
              <Text>navigation.faq__mobile</Text>
            </span>
            {access.hasAnyAccess() && (
              <>
                <a href="/my/" className="header__link-mobile">
                  <Text>navigation.my_surveys</Text>
                </a>
                <a href="/my/?dialog=profile" className="header__link-mobile">
                  <Text>navigation.my_profile</Text>
                </a>
                <a href="/my/?dialog=transactions" className="header__link-mobile">
                  <Text>navigation.transactions</Text>
                </a>
                <a className="header__link-mobile" onClick={handleLogout}>
                  <Text>navigation.logout</Text>
                </a>
              </>
            )}
          </div>
          {!access.hasAnyAccess() && (
            <UnauthorizedUserMobileMenu onLogin={handleLoginDialog} onSignUp={handleSignUpDialog} />
          )}
        </div>
      </header>
    );
  }
);
